/**
   Flatpickr Timezone Conversion Utilities

   Flatpickr uses JavaScript Date objects internally, which are always interpreted
   in the browser's local timezone. This creates issues when we want to display
   and edit times in a different timezone than the user's browser.

   To fix this, we "trick" flatpickr by creating Date objects that represent the target
   timezone's local time as if it were the browser's local time. This way, flatpickr's
   time picker displays the correct hours/minutes for our target timezone.

   For example, if we want to show 3:00 PM Pacific time:
   - Instead of passing a Date that represents 3:00 PM Pacific in UTC to flatpickr,
   - We create a Date with literal values (15, 0, 0) for hours/minutes/seconds
   - This makes flatpickr's time picker show "3:00 PM" regardless of browser timezone

   We then convert this back to a Moment with the correct timezone before sending
   anywhere else in the app.

   Example scenario: User in New York (Eastern) is viewing a worksheet that uses Pacific
   time and sets the start date to 3:00PM
   Without timezone handling:
   - We pass 3:00 PM Pacific (23:00 UTC) as a JS Date
   - Browser interprets this as 6:00 PM EST (same UTC moment)
   - Flatpickr shows 6:00 PM in the time picker ❌

   With our timezone handling:
   - We create a "naive" Date with literal values: new Date(year, month, day, 15, 0, 0)
   - Flatpickr shows 3:00 PM in the time picker ✅
   - When user selects time, we convert back to proper Pacific timezone moment
 **/

import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import type { Timezone } from '@/datetime/timezone.service';

/**
  Converts a Moment in a specific timezone to a "timezone-naive" JavaScript Date.

  A "timezone-naive" date contains the literal clock time values (hour, minute, second)
  from the target timezone, but is interpreted by JavaScript as if it were in the
  browser's local timezone. This is necessary because JavaScript interprets all Date objects
  as local time, but we want to display time in a different timezone.
**/
export const momentToTimezoneNaiveDate = (value: Moment, timezone: Timezone): Date => {
  const targetDateTime = moment(value).tz(timezone.name);
  return new Date(
    targetDateTime.year(),
    targetDateTime.month(),
    targetDateTime.date(),
    targetDateTime.hour(),
    targetDateTime.minute(),
    targetDateTime.second(),
    targetDateTime.millisecond(),
  );
};

/**
 Converts a "timezone-naive" JavaScript Date back to a Moment in the specified timezone.

 This reverses the conversion done by momentToTimezoneNaiveDate. It takes the literal
 time values from the Date object and interprets them as local time in the target timezone.
 */
export const timezoneNaiveDateToMoment = (date: Date, timezone: Timezone): Moment => {
  return moment.tz(
    [
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ],
    timezone.name,
  );
};

/**
 * Checks if two dates represent the same moment when formatted in the given timezone
 */
export const areFormattedDatesEqual = (
  currentValue: string,
  expectedMoment: Moment,
  timezone: Timezone,
  format: string,
): boolean => {
  const expectedValue = expectedMoment.tz(timezone.name).format(format);
  return currentValue === expectedValue;
};
